import { useStoreMutation } from '@client/queries/useStoreMutation';
import { useStoreQuery } from '@client/queries/useStoreQuery';
import {
  getCreditsBalance,
  getCreditsLedger,
  grantCredits,
  type ICreditsLedgerResponse,
  type IGrantCreditsPayload,
} from '@client/services/credits.service';

import type { IWallet } from '@common/types';

interface IWalletFilter {
  tenantId?: string;
  userId?: string;
}

export const creditsKeys = {
  all: ['credits'] as const,
  balance: (filter?: IWalletFilter) =>
    [...creditsKeys.all, 'balance', filter ?? {}] as const,
  ledgers: () => [...creditsKeys.all, 'ledger'] as const,
  ledger: (params: { limit: number; offset: number } & IWalletFilter) =>
    [...creditsKeys.ledgers(), params] as const,
};

export const useCreditsQueries = () => {
  const useBalance = (filter?: IWalletFilter) =>
    useStoreQuery<IWallet>({
      queryKey: creditsKeys.balance(filter),
      queryFn: () => getCreditsBalance(filter?.tenantId, filter?.userId),
    });

  const useLedger = (limit = 50, offset = 0, filter?: IWalletFilter) =>
    useStoreQuery<ICreditsLedgerResponse>({
      queryKey: creditsKeys.ledger({ limit, offset, ...filter }),
      queryFn: () =>
        getCreditsLedger(limit, offset, filter?.tenantId, filter?.userId),
    });

  const useGrant = () =>
    useStoreMutation<IWallet, unknown, IGrantCreditsPayload>({
      mutationFn: (payload: IGrantCreditsPayload) => grantCredits(payload),
      invalidateQueries: [creditsKeys.all],
    });

  return { useBalance, useLedger, useGrant };
};
